import React, { useState, useMemo, useCallback } from "react";
import { FlatList, ListRenderItem } from "react-native";

import { Box } from "../../../primitive";
import { CountryCode } from "../utils/countryCodes";
import { CountryCodeItem } from "./CountryCodeItem";

type VirtualizedCountryCodeListProps = {
  countryCodes: CountryCode[];
  selectedCountryCode: CountryCode;
  onCountryCodePress: (countryCode: CountryCode) => void;
};

const ITEM_HEIGHT = 42;

export const VirtualizedCountryCodeList = React.memo(
  ({
    countryCodes,
    selectedCountryCode,
    onCountryCodePress,
  }: VirtualizedCountryCodeListProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    // Sort country codes once and memoize the result
    const sortedCountryCodes = useMemo(() => {
      return [...countryCodes].sort((a, b) => a.name.localeCompare(b.name));
    }, [countryCodes]);

    const handleHoverIn = useCallback((index: number) => {
      setHoveredIndex(index);
    }, []);

    const handleHoverOut = useCallback(() => {
      setHoveredIndex(null);
    }, []);

    const renderItem: ListRenderItem<CountryCode> = useCallback(
      ({ item, index }) => (
        <CountryCodeItem
          ITEM_HEIGHT={ITEM_HEIGHT}
          countryCode={item}
          isSelected={selectedCountryCode.id === item.id}
          isHovered={hoveredIndex === index}
          onPress={onCountryCodePress}
          onHoverIn={() => handleHoverIn(index)}
          onHoverOut={handleHoverOut}
        />
      ),
      [
        selectedCountryCode.id,
        hoveredIndex,
        onCountryCodePress,
        handleHoverIn,
        handleHoverOut,
      ]
    );

    const getItemLayout = (
      _: ArrayLike<CountryCode> | null | undefined,
      index: number
    ) => ({
      length: ITEM_HEIGHT,
      offset: ITEM_HEIGHT * index,
      index,
    });

    const keyExtractor = (item: CountryCode) => item.id;

    return (
      <Box padding={2} borderRadius="6" bg="surface-primary">
        <FlatList
          data={sortedCountryCodes}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          getItemLayout={getItemLayout}
          initialNumToRender={10}
          maxToRenderPerBatch={20}
          windowSize={10}
          removeClippedSubviews={true}
          showsVerticalScrollIndicator={false}
        />
      </Box>
    );
  }
);
